import { useState } from "react";
import { Stack, TextField, Collapse, Box, Alert, Typography } from "@mui/material";

import * as yup from "yup";
import { useFormik } from "formik";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useDialogs } from "@toolpad/core/useDialogs";
import { resetPassword, signIn, signOut } from "aws-amplify/auth";

import { EMAIL, PASSWORD } from "@helpers/regex";
import { inputConvertToLowercase } from "@helpers/generals";
import { setRecoveryUsername } from "@store/slices/sessionSlice";
import { loginSuccess, setSignInStep } from "@store/slices/sessionSlice";
import { Notificacion } from "@components/DialogosSegmentos/Notificacion";

import { ArrowRight } from "lucide-react";

import { AppButton } from "@components/v2/AppButton";
import { LoginSwitch } from "./LoginSwitch";
import { PasswordField } from "./PasswordField";
import { dividerLinksBlockSx, linkSx, primaryButtonSx } from "./loginStyles";

export const FormularioInicioSesion = () => {
    const dialogs = useDialogs();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const [error, setError] = useState<boolean>(false);
    const [errroMsg, setErrorMsg] = useState<string>();
    const [rememberMe, setRememberMe] = useState<boolean>(false);

    const formik = useFormik({
        initialValues: {
            email: "",
            password: "",
        },
        validationSchema: yup.object().shape({
            email: yup
                .string()
                .matches(EMAIL, "Asegúrate de incluir el @ y los puntos necesarios (.com .mx)")
                .required("Campo requerido"),
            password: yup
                .string()
                .matches(
                    PASSWORD,
                    "Formato incorrecto. Debe contener una letra mayúscula, número, carácter especial y más de 8 caracteres.",
                )
                .required("Campo requerido"),
        }),
        onSubmit: async (values, { setSubmitting }) => {
            try {
                setSubmitting(true);
                setError(false);
                setErrorMsg(undefined);

                const signedResult = await signIn({
                    username: values.email,
                    password: values.password,
                    options: {
                        authFlowType: "USER_SRP_AUTH",
                    },
                });

                if (signedResult.isSignedIn && signedResult.nextStep.signInStep == "DONE") {
                    if (rememberMe) {
                        if (formik.values.email == localStorage.getItem("email")) {
                            localStorage.removeItem("email");
                        }
                    } else {
                        localStorage.setItem("email", formik.values.email);
                    }

                    dispatch(loginSuccess());
                    dispatch(setSignInStep("MFA"));

                    // TODO Para brincar validación OTP
                    // dispatch(verifiedOTP());
                    // dispatch(setSignInStep("WELCOME"));
                    // navigate("/solicitudes");
                } else if (signedResult.nextStep.signInStep === "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED") {
                    // navigate("/cambiar_contrasenia");
                    dispatch(loginSuccess());
                    dispatch(setSignInStep("NEW_PASSWORD"));
                }
            } catch (error: any) {
                if (error.name === "NotAuthorizedException") {
                    setError(true);
                    setErrorMsg("Credenciales incorrectas. Verifica tu correo electrónico y contraseña.");
                }

                if (error.name === "UserNotFoundException") {
                    setError(true);
                    setErrorMsg("Verifica tu correo electrónico y contraseña.");
                }

                if (error.status === 404) {
                    setError(true);
                    setErrorMsg(error.data.message);
                }

                if (error.status == 401 && error.data.message == "ERREXPSW") {
                    setError(true);
                    setErrorMsg(error.data.errors);
                }

                await signOut();
                setTimeout(() => {
                    setError(false);
                }, 2000);
            } finally {
                setSubmitting(false);
            }
        },
    });

    const handleRecoveryAccountWithCode = async () => {
        dispatch(setRecoveryUsername(""));
        dispatch(setSignInStep("RESET_PASS"));
        // navigate("/recuperar_cuenta");
    };

    const handleRecoveryAccount = async () => {
        if (formik.values.email.trim().length !== 0 && Boolean(formik.errors.email) == false) {
            await resetPassword({ username: formik.values.email });
            // setShowModalRecoveryCode(true);
            await dialogs.open(Notificacion, {
                type: "INFO",
                text: `Se ha enviado un código de verificación al correo ${formik.values.email}`,
            });
        }
    };

    const handleOnChangeRememberMe = (checked: boolean) => {
        setRememberMe(!checked);
    };

    return (
        <form onSubmit={formik.handleSubmit} noValidate>
            <Stack direction={"column"} sx={{ gap: 2.5 }}>
                <Box>
                    <Typography
                        sx={{
                            fontFamily: "'Outfit', system-ui, sans-serif",
                            fontSize: "32px",
                            fontWeight: 600,
                            letterSpacing: "-0.02em",
                            color: "text.primary",
                        }}>
                        ¡Hola!
                    </Typography>
                    <Typography sx={{ fontSize: "15px", color: "text.secondary", mt: 1 }}>
                        Ingresa tu correo y tu contraseña
                    </Typography>
                </Box>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
                    <Typography sx={{ fontSize: "13px", fontWeight: 600, color: "text.primary" }}>
                        Correo electrónico
                    </Typography>
                    <TextField
                        name="email"
                        data-testid="email-login"
                        fullWidth
                        required
                        size="small"
                        onChange={(event) => inputConvertToLowercase(event, formik)}
                        onBlur={formik.handleBlur}
                        error={(formik.touched.email && Boolean(formik.errors.email)) ?? false}
                        helperText={formik.touched.email && formik.errors.email}
                        value={formik.values.email}
                        slotProps={{ htmlInput: { sx: { height: 48, boxSizing: "border-box" } } }}
                    />
                </Box>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                        <Typography sx={{ fontSize: "13px", fontWeight: 600, color: "text.primary" }}>
                            Contraseña
                        </Typography>
                        <Typography sx={{ fontSize: "13px", ...linkSx }} onClick={handleRecoveryAccount}>
                            Olvidé mi contraseña
                        </Typography>
                    </Box>
                    <PasswordField
                        name="password"
                        data-testid="password-login"
                        fullWidth
                        size="small"
                        required
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={(formik.touched.password && Boolean(formik.errors.password)) ?? false}
                        helperText={formik.touched.password && formik.errors.password}
                        value={formik.values.password}
                    />
                </Box>

                <LoginSwitch
                    checked={!rememberMe}
                    onChange={handleOnChangeRememberMe}
                    label="Recordar usuario en este dispositivo"
                />

                <Collapse in={error && errroMsg !== undefined}>
                    <Alert variant="outlined" severity="error" sx={{ textAlign: "center" }}>
                        {errroMsg}
                    </Alert>
                </Collapse>

                <AppButton
                    tone="navy"
                    type="submit"
                    data-testid="submit-login"
                    size="large"
                    loadingPosition="end"
                    loading={formik.isSubmitting}
                    endIcon={<ArrowRight size={18} strokeWidth={2.2} />}
                    sx={primaryButtonSx}
                >
                    Iniciar sesión
                </AppButton>

                <Box sx={dividerLinksBlockSx}>
                    <Typography sx={{ fontSize: "13px", color: "text.secondary" }}>
                        ¿Tienes un código de recuperación?{" "}
                        <Typography component="span" sx={{ fontSize: "13px", ...linkSx }} onClick={handleRecoveryAccountWithCode}>
                            Ingrésalo aquí
                        </Typography>
                    </Typography>
                </Box>
            </Stack>
        </form>
    );
};
